import { RRule, Frequency, rrulestr, datetime } from 'rrule';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

export type FrequencyType =
  | 'one-off'
  | 'everyday'
  | 'weekday'
  | 'weekly'
  | 'monthly-date'
  | 'monthly'
  | 'every-3-months'
  | 'every-6-months'
  | 'yearly'
  | 'every-3-years';

export interface FrequencyOption {
  value: FrequencyType;
  label: string;
}

const formatMsIso = (ms: number): string => {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `PT${hours}H${minutes}M`;
};

export function formatDurationIso(startDate: Date, endDate: Date): string {
  return formatMsIso(endDate.getTime() - startDate.getTime());
}

const WALL_CLOCK_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

// The instant's wall clock in `zone`, as if that wall clock were UTC: differences between two are wall-clock time.
const wallClockMs = (date: Date, zone: string): number => date.getTime() + dayjs(date).tz(zone).utcOffset() * 60000;

const fromWallClockMs = (ms: number, zone: string): Date =>
  dayjs.tz(dayjs.utc(ms).format(WALL_CLOCK_FORMAT), zone).toDate();

// A duration counts wall-clock time, so a cycle across a clock change keeps the times picked.
export function wallClockGap(startDate: Date, endDate: Date, zone?: string): number {
  return zone ? wallClockMs(endDate, zone) - wallClockMs(startDate, zone) : endDate.getTime() - startDate.getTime();
}

export function addWallClockMs(startDate: Date, ms: number, zone?: string): Date {
  return zone ? fromWallClockMs(wallClockMs(startDate, zone) + ms, zone) : new Date(startDate.getTime() + ms);
}

export function wallClockDuration(startDate: Date, endDate: Date, zone: string): string {
  return formatMsIso(wallClockGap(startDate, endDate, zone));
}

export function addWallClock(startDate: Date, isoDuration: string, zone: string): Date {
  return addWallClockMs(startDate, dayjs.duration(isoDuration).asMilliseconds(), zone);
}

export function sameWallClockIn(date: Date, from: string, to: string): Date {
  return fromWallClockMs(wallClockMs(date, from), to);
}

export function atWallClock(day: string, time: string, zone: string): Date {
  return dayjs.tz(`${day}T${time}:00`, zone).toDate();
}

// rrule.js reads a TZID start as a floating date whose UTC fields hold the wall clock in that zone.
export function ruleStart(rule: RRule): Date {
  const { dtstart, tzid } = rule.options;
  if (!tzid || tzid === 'UTC') {
    return new Date(dtstart);
  }
  return dayjs.tz(dayjs.utc(dtstart).format(WALL_CLOCK_FORMAT), tzid).toDate();
}

interface StartParts {
  dtstart: Date;
  tzid?: string;
  weekday: number;
  dayOfMonth: number;
  month: number;
}

// Without a zone the start is the UTC instant; its weekday and day are `dayZone`'s, else the browser's.
const startParts = (startDate: Date, zone?: string, dayZone?: string): StartParts => {
  if (!zone) {
    const day = dayZone ? dayjs(startDate).tz(dayZone) : dayjs(startDate);
    return {
      dtstart: dayjs(startDate).utc().toDate(),
      weekday: day.day(),
      dayOfMonth: day.date(),
      month: day.month() + 1,
    };
  }
  const wall = dayjs(startDate).tz(zone);
  return {
    dtstart: datetime(wall.year(), wall.month() + 1, wall.date(), wall.hour(), wall.minute(), wall.second()),
    tzid: zone,
    weekday: wall.day(),
    dayOfMonth: wall.date(),
    month: wall.month() + 1,
  };
};

// Noon, so the browser's own clock change can never move the day the labels read.
const zoneDay = (startDate: Date, zone?: string): Date => {
  if (!zone) return startDate;
  const wall = dayjs(startDate).tz(zone);
  return new Date(wall.year(), wall.month(), wall.date(), 12);
};

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function getWeekdayFromDate(date: Date): string {
  return WEEKDAY_NAMES[date.getDay()];
}

export function getWeekdayOrdinal(date: Date): number {
  const dayOfMonth = date.getDate();
  return Math.ceil(dayOfMonth / 7);
}

function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

function getDateOrdinalLabel(date: Date): string {
  const day = date.getDate();
  return `${day}${getOrdinalSuffix(day)}`;
}

function getOrdinalLabel(num: number): string {
  const labels = ['first', 'second', 'third', 'fourth', 'fifth'];
  return labels[num - 1] || `${num}${getOrdinalSuffix(num)}`;
}

export function getFrequencyOptions(
  startDate: Date | null,
  includeEveryThreeYears: boolean = false,
  zone?: string
): FrequencyOption[] {
  const baseOptions: FrequencyOption[] = [
    { value: 'one-off', label: 'One-off' },
    { value: 'everyday', label: 'Everyday' },
    { value: 'weekday', label: 'Every weekday' },
  ];

  if (!startDate) {
    const options: FrequencyOption[] = [
      ...baseOptions,
      { value: 'weekly', label: 'Weekly' },
      { value: 'monthly-date', label: 'Monthly on date' },
      { value: 'monthly', label: 'Monthly on weekday' },
      { value: 'every-3-months', label: 'Every 3 months' },
      { value: 'every-6-months', label: 'Every 6 months' },
      { value: 'yearly', label: 'Yearly' },
    ];
    if (includeEveryThreeYears) {
      options.push({ value: 'every-3-years', label: 'Every 3 years' });
    }
    return options;
  }

  const labelDate = zoneDay(startDate, zone);
  const weekday = getWeekdayFromDate(labelDate);
  const ordinal = getWeekdayOrdinal(labelDate);
  const ordinalLabel = getOrdinalLabel(ordinal);
  const dateOrdinalLabel = getDateOrdinalLabel(labelDate);
  const month = MONTH_NAMES[labelDate.getMonth()];
  const day = labelDate.getDate();

  const options: FrequencyOption[] = [
    ...baseOptions,
    { value: 'weekly', label: `Weekly on ${weekday}` },
    { value: 'monthly-date', label: `Monthly on the ${dateOrdinalLabel}` },
    { value: 'monthly', label: `Monthly on the ${ordinalLabel} ${weekday}` },
    { value: 'every-3-months', label: `Every 3 months on the ${dateOrdinalLabel}` },
    { value: 'every-6-months', label: `Every 6 months on the ${dateOrdinalLabel}` },
    { value: 'yearly', label: `Annually on ${month} ${day}` },
  ];
  if (includeEveryThreeYears) {
    options.push({ value: 'every-3-years', label: `Every 3 years on ${month} ${day}` });
  }
  return options;
}

function formatWithUtcTzid(rruleString: string): string {
  // The rrule library outputs UTC as "DTSTART:20241218T100000Z"
  // but backend expects "DTSTART;TZID=UTC:20241218T100000"
  return rruleString.replace(/DTSTART:(\d{8}T\d{6})Z/, 'DTSTART;TZID=UTC:$1');
}

const RRULE_WEEKDAYS = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];

// With a zone, every part is read on the zone's wall clock: a Monday 00:30 in London is BYDAY=MO.
export function generateRRule(frequency: FrequencyType, startDate: Date, zone?: string, dayZone?: string): string {
  const { dtstart, tzid, weekday, dayOfMonth, month } = startParts(startDate, zone, dayZone);
  const build = (options: Partial<ConstructorParameters<typeof RRule>[0]>): string =>
    formatWithUtcTzid(new RRule({ ...options, dtstart, tzid }).toString());

  switch (frequency) {
    case 'one-off':
      return build({ freq: Frequency.DAILY, count: 1 });

    case 'everyday':
      return build({ freq: Frequency.DAILY, interval: 1 });

    case 'weekday':
      return build({ freq: Frequency.DAILY, byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR] });

    case 'weekly':
      return build({ freq: Frequency.WEEKLY, byweekday: [RRULE_WEEKDAYS[weekday]] });

    case 'monthly-date':
      return build({ freq: Frequency.MONTHLY, interval: 1, bymonthday: dayOfMonth });

    case 'every-3-months':
      return build({ freq: Frequency.MONTHLY, interval: 3, bymonthday: dayOfMonth });

    case 'every-6-months':
      return build({ freq: Frequency.MONTHLY, interval: 6, bymonthday: dayOfMonth });

    case 'monthly':
      return build({
        freq: Frequency.MONTHLY,
        byweekday: [RRULE_WEEKDAYS[weekday].nth(Math.ceil(dayOfMonth / 7))],
      });

    case 'yearly':
      return build({ freq: Frequency.YEARLY, bymonth: month, bymonthday: dayOfMonth });

    case 'every-3-years':
      return build({ freq: Frequency.YEARLY, interval: 3, bymonth: month, bymonthday: dayOfMonth });

    default:
      return '';
  }
}

export function isWeekdayPattern(freq: Frequency, byweekday: unknown): boolean {
  if (freq !== Frequency.DAILY) {
    return false;
  }

  if (!Array.isArray(byweekday) || byweekday.length !== 5) {
    return false;
  }

  // When parsed from string, byweekday is an array of numbers [0,1,2,3,4] for MO,TU,WE,TH,FR
  // When created programmatically, it's an array of Weekday objects
  const weekdayValues = new Set(
    byweekday
      .map((day) => {
        if (typeof day === 'number') {
          return day;
        }
        // Weekday object has a weekday property
        if (day && typeof day === 'object' && 'weekday' in day) {
          return (day as { weekday: number }).weekday;
        }
        return null;
      })
      .filter((val): val is number => val !== null)
  );

  // Check if we have exactly the 5 weekdays: MO(0), TU(1), WE(2), TH(3), FR(4)
  const expectedWeekdays = [0, 1, 2, 3, 4];
  return weekdayValues.size === 5 && expectedWeekdays.every((day) => weekdayValues.has(day));
}

export function parseRRuleToFrequency(rruleString: string): FrequencyType {
  if (!rruleString) {
    return 'one-off';
  }

  try {
    const rule = rrulestr(rruleString);
    const { freq, count, byweekday } = rule.options;

    if (count === 1) {
      return 'one-off';
    }

    switch (freq) {
      case Frequency.DAILY:
        if (isWeekdayPattern(freq, byweekday)) {
          return 'weekday';
        }
        return 'everyday';

      case Frequency.WEEKLY:
        return 'weekly';

      case Frequency.MONTHLY:
        if (rule.options.bymonthday?.length) {
          const interval = rule.options.interval ?? 1;
          if (interval === 3) return 'every-3-months';
          if (interval === 6) return 'every-6-months';
          return 'monthly-date';
        }
        return 'monthly';

      case Frequency.YEARLY: {
        const interval = rule.options.interval ?? 1;
        if (interval === 3) return 'every-3-years';
        return 'yearly';
      }

      default:
        return 'one-off';
    }
  } catch {
    return 'one-off';
  }
}
